import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const jazzAJuan: ChauffeurPage = {
  slug: "jazz-a-juan",
  title: "Chauffeur privé Jazz à Juan – VTC Antibes Juan-les-Pins",
  subtitle:
    "Chauffeur privé pendant Jazz à Juan : accès à la pinède Gould, transferts depuis l'aéroport de Nice et retours de nuit après les concerts à Juan-les-Pins.",
  keywords: [
    "chauffeur jazz à juan",
    "vtc jazz à juan",
    "chauffeur privé juan-les-pins",
  ],
  hero: {
    title: "Chauffeur privé pendant Jazz à Juan",
    subtitle:
      "Concerts à la pinède Gould, face à la mer : un chauffeur dédié quand Juan-les-Pins sature et que les places de stationnement manquent.",
    keywords: [
      "chauffeur Jazz à Juan",
      "VTC pinède Gould",
      "transfert aéroport Nice Juan-les-Pins",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le festival Jazz à Juan",
    badges: ["Accueil aéroport", "Dépose pinède Gould", "Retours de nuit"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant Jazz à Juan",
    items: [
      {
        title: "Juan-les-Pins sans stationnement",
        description:
          "En juillet, la station balnéaire est saturée et les places manquent près de la pinède Gould : le chauffeur vous dépose à l'entrée et repart.",
      },
      {
        title: "Aéroport de Nice géré",
        description:
          "L'aéroport est à une vingtaine de kilomètres. Accueil pancarte, suivi de vol et transfert direct vers votre hôtel d'Antibes ou du Cap.",
      },
      {
        title: "Concerts face à la mer",
        description:
          "La pinède Gould joue tard : le chauffeur patiente et assure le retour dès la fin du concert, sans chercher de taxi.",
      },
      {
        title: "5,0/5 sur 38 avis Google",
        description:
          "Ponctualité, discrétion et véhicules soignés : la régularité qui vaut à KS Rent Services une note de 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant Jazz à Juan",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert Nice ↔ Juan-les-Pins / Antibes avec suivi de vol pendant l'affluence de juillet.",
      },
      {
        title: "Dépose à la pinède Gould",
        text: "Approche au plus près de l'entrée du festival selon la circulation et l'horaire des concerts.",
      },
      {
        title: "Mise à disposition soirée",
        text: "Chauffeur dédié pour le dîner en bord de mer puis le concert, avec attente sur place.",
      },
      {
        title: "Retours de nuit",
        text: "Retour direct à l'hôtel sur Antibes, le Cap d'Antibes, Cannes ou Nice après le concert.",
      },
      {
        title: "Navettes groupes & invités",
        text: "Coordination multi-véhicules pour familles, groupes et invités.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant Jazz à Juan",
    items: [
      {
        name: "Pinède Gould",
        description:
          "Scène mythique en plein air, face à la baie de Juan-les-Pins.",
      },
      {
        name: "Juan-les-Pins",
        description:
          "Front de mer, casino, restaurants et hôtels de la station.",
      },
      {
        name: "Antibes & Cap d'Antibes",
        description: "Vieil Antibes, port Vauban et résidences du Cap.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "À une vingtaine de kilomètres, terminaux 1 et 2.",
      },
      {
        name: "Gare d'Antibes",
        description: "Arrivées en train, à quelques minutes de la pinède.",
      },
    ],
  },
  generic: {
    heading: "Jazz à Juan vu d'un chauffeur",
    subheading:
      "Chaque été en juillet, Juan-les-Pins accueille l'un des plus anciens festivals de jazz d'Europe, à la pinède Gould face à la mer.",
    paragraphs: [
      "Chaque année en juillet, Jazz à Juan installe sa scène à la pinède Gould, au cœur de Juan-les-Pins. La station est alors bondée, la circulation dense et le stationnement rare : un chauffeur vous dépose au plus près de l'entrée et vous évite de tourner pendant de longues minutes.",
      "La plupart des visiteurs arrivent par l'aéroport de Nice Côte d'Azur, à une vingtaine de kilomètres. Avec le suivi de vol et l'accueil pancarte, le transfert vers votre hôtel d'Antibes, du Cap d'Antibes ou de Juan-les-Pins se fait sans attente.",
      "Les concerts de la pinède se terminent tard dans la nuit. La mise à disposition garde le même chauffeur pour le dîner en bord de mer puis le concert, et garantit un retour direct dès la dernière note.",
    ],
    bullets: [
      "Dépose au plus près de la pinède",
      "Aéroport de Nice à ~20 km",
      "Retours après les concerts",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour Jazz à Juan",
    steps: [
      "Indiquez vos soirs de concert, votre hôtel et vos horaires.",
      "Nous calons les déposes selon la circulation à Juan-les-Pins et l'accès à la pinède.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir dîner, concert et retour.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pendant Jazz à Juan ?",
      a: "Réservez à l'avance : en juillet, Juan-les-Pins et Antibes sont pris d'assaut et la demande grimpe les soirs de concert. Précisez vos dates, votre hôtel et vos horaires pour que nous bloquions un chauffeur dédié.",
    },
    {
      q: "Où le chauffeur peut-il me déposer près de la pinède Gould ?",
      a: "La pinède Gould se trouve en plein cœur de Juan-les-Pins, dans un secteur très fréquenté en soirée. Le chauffeur vous dépose au point autorisé le plus proche de l'entrée, puis vous reprend au même endroit à la fin du concert.",
    },
    {
      q: "Gérez-vous les transferts depuis l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur est à une vingtaine de kilomètres de Juan-les-Pins : nous adaptons l'heure de prise en charge en cas de retard, même en pleine saison.",
    },
    {
      q: "Le stationnement est-il compliqué pendant le festival ?",
      a: "Oui. En juillet, la station est saturée et les places sont rares près de la pinède les soirs de concert. Le chauffeur vous évite de chercher : il dépose, patiente ou revient à l'heure convenue.",
    },
    {
      q: "Assurez-vous les retours tardifs après les concerts ?",
      a: "Oui. Les concerts de la pinède finissent tard et les taxis manquent à la sortie : le chauffeur patiente et garantit votre retour direct vers Antibes, le Cap d'Antibes, Cannes ou Nice.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berlines, SUV et vans avec chauffeur selon la taille de votre groupe, pour un trajet confortable entre l'hôtel, le dîner et la pinède Gould.",
    },
    {
      q: "Pouvez-vous prendre en charge une famille ou un groupe ?",
      a: "Oui. Nous coordonnons plusieurs véhicules pour les familles et groupes d'amis, avec le même niveau de ponctualité pour tout le monde.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un chauffeur à Antibes",
      href: "/chauffeur/antibes",
    },
    {
      label: "Voiture avec chauffeur à Nice",
      href: "/chauffeur/nice",
    },
    {
      label: "Accueil à l’aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Chauffeur Côte d’Azur, tarifs et trajets",
      href: "/chauffeur/cote-d-azur",
    },
  ],
  cta: {
    title: "Votre chauffeur pour Jazz à Juan",
    text: "Accueil aéroport, dépose à la pinède Gould et retours de nuit : réservez un chauffeur dédié pour vos soirées de concert.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur privé Jazz à Juan Antibes | KS Rent",
    description:
      "Chauffeur privé pendant Jazz à Juan : transfert aéroport de Nice, dépose à la pinède Gould à Juan-les-Pins et retours de nuit après les concerts. Réservez.",
    canonical: "/chauffeur/jazz-a-juan",
  },
};

export default jazzAJuan;
